import { useEffect, useState, useCallback } from "react";
import { Save, RotateCcw, Plus, Trash2, Wallet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";

const PERIODICITIES = [
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quincenal" },
  { value: "monthly", label: "Mensual" },
];

const WEEK_DAYS = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
];

const INCIDENT_TYPES = [
  { key: "ausencia_justificada",   label: "Ausencia justificada" },
  { key: "ausencia_injustificada", label: "Ausencia injustificada" },
  { key: "incapacidad_ccss",       label: "Incapacidad CCSS" },
  { key: "incapacidad_ins",        label: "Incapacidad INS (Riesgos)" },
  { key: "vacaciones",             label: "Vacaciones" },
  { key: "permiso_con_goce",       label: "Permiso con goce" },
  { key: "permiso_sin_goce",       label: "Permiso sin goce" },
];

const blankBracket = () => ({ limit: 0, rate: 0 });

export default function PayrollConfig({ embedded = false }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/payroll-config");
      setCfg(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la configuración");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (field, value) => setCfg((c) => ({ ...c, [field]: value }));

  const updateBracket = (idx, field, value) => {
    setCfg((c) => {
      const list = [...(c.tax_brackets || [])];
      list[idx] = { ...list[idx], [field]: value };
      return { ...c, tax_brackets: list };
    });
  };

  const addBracket = () => {
    setCfg((c) => ({ ...c, tax_brackets: [...(c.tax_brackets || []), blankBracket()] }));
  };

  const removeBracket = (idx) => {
    setCfg((c) => ({
      ...c,
      tax_brackets: (c.tax_brackets || []).filter((_, i) => i !== idx),
    }));
  };

  const updateIncidentRule = (key, field, value) => {
    setCfg((c) => {
      const cur = c.incident_pay_rules?.[key] || { first_n_days: 0, first_pct: 0, remainder_pct: 0 };
      return {
        ...c,
        incident_pay_rules: {
          ...(c.incident_pay_rules || {}),
          [key]: { ...cur, [field]: value },
        },
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Normalize numeric strings
      const payload = {
        ...cfg,
        week_start_day: Number(cfg.week_start_day),
        month_start_day: Number(cfg.month_start_day),
        second_period_start_day: Number(cfg.second_period_start_day),
        standard_hours_per_day: Number(cfg.standard_hours_per_day),
        ccss_employee_pct: Number(cfg.ccss_employee_pct),
        ccss_employer_pct: Number(cfg.ccss_employer_pct),
        overtime_multiplier: Number(cfg.overtime_multiplier),
        holiday_multiplier: Number(cfg.holiday_multiplier),
        monthly_tax_exempt: Number(cfg.monthly_tax_exempt),
        work_hours_per_month: Number(cfg.work_hours_per_month),
        tax_brackets: (cfg.tax_brackets || []).map((b) => ({
          limit: Number(b.limit),
          rate: Number(b.rate),
        })),
        vacation_days_per_year: Number(cfg.vacation_days_per_year ?? 12),
        vacation_workweek_days: Number(cfg.vacation_workweek_days ?? 6),
        incident_pay_rules: Object.fromEntries(
          INCIDENT_TYPES.map((t) => {
            const r = cfg.incident_pay_rules?.[t.key] || {};
            return [t.key, {
              first_n_days: Number(r.first_n_days || 0),
              first_pct: Number(r.first_pct || 0),
              remainder_pct: Number(r.remainder_pct || 0),
            }];
          })
        ),
      };
      const res = await api.put("/payroll-config", payload);
      setCfg(res.data);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("¿Restablecer todos los valores a los predeterminados?")) return;
    try {
      const res = await api.post("/payroll-config/reset");
      setCfg(res.data);
      toast.success("Valores restablecidos");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo restablecer");
    }
  };

  if (loading || !cfg) {
    return (
      <div className="py-12 text-center text-sm text-zinc-400" data-testid="payroll-config-loading">
        Cargando configuración…
      </div>
    );
  }

  return (
    <div data-testid="payroll-config-page" className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Configuración de Nómina</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Define la periodicidad, cortes, jornada estándar, porcentajes CCSS, multiplicadores y brackets de renta.
          </p>
        </div>
      )}

      {/* SECTION: Periodicidad */}
      <Section title="Periodicidad y cortes">
        <Field label="Periodicidad de pago">
          <Select value={cfg.payment_periodicity} onValueChange={(v) => update("payment_periodicity", v)}>
            <SelectTrigger data-testid="payroll-cfg-periodicity"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODICITIES.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Día inicial de la semana laboral">
          <Select
            value={String(cfg.week_start_day)}
            onValueChange={(v) => update("week_start_day", Number(v))}
          >
            <SelectTrigger data-testid="payroll-cfg-week-start"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WEEK_DAYS.map((d) => (
                <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Día inicial del mes (1ª quincena)" hint="Día del mes en que inicia el primer corte.">
          <Input
            type="number" min={1} max={28}
            value={cfg.month_start_day}
            onChange={(e) => update("month_start_day", e.target.value)}
            data-testid="payroll-cfg-month-start"
          />
        </Field>
        <Field label="Día inicial de la 2ª quincena" hint="Día del mes en que inicia el segundo corte.">
          <Input
            type="number" min={2} max={28}
            value={cfg.second_period_start_day}
            onChange={(e) => update("second_period_start_day", e.target.value)}
            data-testid="payroll-cfg-second-start"
          />
        </Field>
        <Field label="Día de pago" hint="Texto libre. Ej.: 'Cada 15 y último', 'Viernes', etc.">
          <Input
            value={cfg.payment_day_rule || ""}
            onChange={(e) => update("payment_day_rule", e.target.value)}
            data-testid="payroll-cfg-pay-rule"
          />
        </Field>
      </Section>

      {/* SECTION: Jornada */}
      <Section title="Jornada laboral">
        <Field label="Horas estándar por día">
          <Input
            type="number" step="0.25" min={0}
            value={cfg.standard_hours_per_day}
            onChange={(e) => update("standard_hours_per_day", e.target.value)}
            data-testid="payroll-cfg-hours-day"
          />
        </Field>
        <Field label="Horas laborales / mes" hint="Usado para derivar tarifa horaria cuando solo hay salario mensual.">
          <Input
            type="number" step="1" min={0}
            value={cfg.work_hours_per_month}
            onChange={(e) => update("work_hours_per_month", e.target.value)}
            data-testid="payroll-cfg-hours-month"
          />
        </Field>
        <Field label="Multiplicador hora extra" hint="Ej.: 1.5 = pago al 150% del valor hora ordinaria.">
          <Input
            type="number" step="0.05" min={1}
            value={cfg.overtime_multiplier}
            onChange={(e) => update("overtime_multiplier", e.target.value)}
            data-testid="payroll-cfg-overtime-mult"
          />
        </Field>
        <Field label="Multiplicador feriado" hint="Ej.: 2.0 = pago doble.">
          <Input
            type="number" step="0.05" min={1}
            value={cfg.holiday_multiplier}
            onChange={(e) => update("holiday_multiplier", e.target.value)}
            data-testid="payroll-cfg-holiday-mult"
          />
        </Field>
      </Section>

      {/* SECTION: CCSS */}
      <Section title="Cargas sociales (CCSS)">
        <Field label="% CCSS obrero" hint="Deducción del trabajador.">
          <Input
            type="number" step="0.01" min={0} max={100}
            value={cfg.ccss_employee_pct}
            onChange={(e) => update("ccss_employee_pct", e.target.value)}
            data-testid="payroll-cfg-ccss-emp"
          />
        </Field>
        <Field label="% CCSS patronal" hint="Costo del patrono (informativo en el cálculo actual).">
          <Input
            type="number" step="0.01" min={0} max={100}
            value={cfg.ccss_employer_pct}
            onChange={(e) => update("ccss_employer_pct", e.target.value)}
            data-testid="payroll-cfg-ccss-emp-r"
          />
        </Field>
      </Section>

      {/* SECTION: Impuesto sobre la renta */}
      <Section title="Impuesto sobre la renta (mensual)">
        <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-4 py-3">
          <div>
            <div className="text-sm font-medium text-zinc-900">Habilitar cálculo de renta</div>
            <div className="text-xs text-zinc-500">Si está deshabilitado, no se descuenta impuesto a ningún trabajador.</div>
          </div>
          <Switch
            checked={!!cfg.income_tax_enabled}
            onCheckedChange={(v) => update("income_tax_enabled", v)}
            data-testid="payroll-cfg-tax-enabled"
          />
        </div>

        <Field label="Mínimo exento mensual (₡)" hint="Ingresos mensuales por debajo de este monto no pagan renta.">
          <Input
            type="number" step="1" min={0}
            value={cfg.monthly_tax_exempt}
            onChange={(e) => update("monthly_tax_exempt", e.target.value)}
            data-testid="payroll-cfg-tax-exempt"
            disabled={!cfg.income_tax_enabled}
          />
        </Field>

        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-wide text-zinc-500">Brackets progresivos</Label>
            <Button
              type="button" variant="outline" size="sm"
              onClick={addBracket}
              disabled={!cfg.income_tax_enabled}
              data-testid="payroll-cfg-bracket-add"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Agregar
            </Button>
          </div>
          <div className="space-y-2">
            {(cfg.tax_brackets || []).map((b, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`payroll-cfg-bracket-${idx}`}>
                <div className="col-span-6">
                  <Label className="text-[10px] text-zinc-400">Hasta (₡)</Label>
                  <Input
                    type="number" step="1" min={0}
                    value={b.limit}
                    onChange={(e) => updateBracket(idx, "limit", e.target.value)}
                    disabled={!cfg.income_tax_enabled}
                    data-testid={`payroll-cfg-bracket-${idx}-limit`}
                  />
                </div>
                <div className="col-span-5">
                  <Label className="text-[10px] text-zinc-400">Tasa (0–1)</Label>
                  <Input
                    type="number" step="0.01" min={0} max={1}
                    value={b.rate}
                    onChange={(e) => updateBracket(idx, "rate", e.target.value)}
                    disabled={!cfg.income_tax_enabled}
                    data-testid={`payroll-cfg-bracket-${idx}-rate`}
                  />
                </div>
                <div className="col-span-1 pt-5">
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeBracket(idx)}
                    disabled={!cfg.income_tax_enabled}
                    data-testid={`payroll-cfg-bracket-${idx}-remove`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {(cfg.tax_brackets || []).length === 0 && (
              <div className="text-xs text-zinc-400 py-2">Sin brackets — agregue al menos uno o deshabilite el impuesto.</div>
            )}
          </div>
          <p className="text-[11px] text-zinc-400 mt-2">
            Tip: la tasa se expresa en fracción decimal (0.10 = 10%). El último bracket cubre montos mayores a su límite.
          </p>
        </div>
      </Section>

      {/* SECTION: Reglas de pago por incidencia */}
      <Section title="Pago por incidencias">
        <div className="md:col-span-2 space-y-3">
          <p className="text-xs text-zinc-500 leading-relaxed">
            Defina qué porcentaje del salario diario paga el patrono por cada tipo de incidencia. Las reglas
            se aplican sobre <strong>días hábiles</strong> (lun-sáb). Ej.: CCSS = 50% los primeros 3 días, 0% después.
          </p>
          <div className="overflow-x-auto rounded-md border border-zinc-200">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-600">
                <tr>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-center px-3 py-2" title="Días iniciales a los que se aplica el % primario">Primeros N días</th>
                  <th className="text-center px-3 py-2">% primario</th>
                  <th className="text-center px-3 py-2" title="Aplica del día N+1 en adelante">% resto</th>
                </tr>
              </thead>
              <tbody>
                {INCIDENT_TYPES.map((t) => {
                  const r = cfg.incident_pay_rules?.[t.key] || { first_n_days: 0, first_pct: 0, remainder_pct: 0 };
                  return (
                    <tr key={t.key} className="border-t border-zinc-100" data-testid={`incident-rule-${t.key}`}>
                      <td className="px-3 py-2 text-zinc-800">{t.label}</td>
                      <td className="px-3 py-2">
                        <Input
                          type="number" min={0} step={1}
                          value={r.first_n_days}
                          onChange={(e) => updateIncidentRule(t.key, "first_n_days", Number(e.target.value))}
                          className="h-8 text-center"
                          data-testid={`incident-rule-${t.key}-days`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number" min={0} max={100} step={1}
                          value={r.first_pct}
                          onChange={(e) => updateIncidentRule(t.key, "first_pct", Number(e.target.value))}
                          className="h-8 text-center"
                          data-testid={`incident-rule-${t.key}-first`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number" min={0} max={100} step={1}
                          value={r.remainder_pct}
                          onChange={(e) => updateIncidentRule(t.key, "remainder_pct", Number(e.target.value))}
                          className="h-8 text-center"
                          data-testid={`incident-rule-${t.key}-rest`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* SECTION: Vacaciones */}
      <Section title="Vacaciones">
        <Field label="Días por año (cada 50 semanas continuas)" hint="CR estatutario: 12 días hábiles. Editable según política interna.">
          <Input
            type="number" min={0} step="0.5"
            value={cfg.vacation_days_per_year ?? 12}
            onChange={(e) => update("vacation_days_per_year", e.target.value)}
            data-testid="payroll-cfg-vac-per-year"
          />
        </Field>
        <Field label="Semana laboral (días hábiles)" hint="6 = lun-sáb (default CR). Usado para vacaciones y reglas de incidencia.">
          <Input
            type="number" min={1} max={7} step={1}
            value={cfg.vacation_workweek_days ?? 6}
            onChange={(e) => update("vacation_workweek_days", e.target.value)}
            data-testid="payroll-cfg-vac-week"
          />
        </Field>
      </Section>

      {/* ACTIONS */}
      <div className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-zinc-200 -mx-4 px-4 py-3 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={handleReset} data-testid="payroll-cfg-reset">
          <RotateCcw className="w-4 h-4 mr-1" /> Restablecer
        </Button>
        <Button onClick={handleSave} disabled={saving} data-testid="payroll-cfg-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">{title}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-zinc-500">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-zinc-400">{hint}</p>}
    </div>
  );
}
